/**
 * THE DRAFT ACCOUNTS (ADR-0019 F) — what a preparation's current version
 * says, set the way a set of draft accounts is set.
 *
 * EVERY FIGURE IS THE SERVER'S. The engine computed every total; the work OS
 * formatted every amount and wrote every sentence (`work.preparation`). This
 * file arranges them and computes nothing: no sum, no sign, no balance check.
 *
 * THE ORDER IS THE READER'S. The answer and its figures first; then what is
 * left for the accountant to decide, with the one act that applies those
 * decisions ("Prepare again"); then the accounts themselves; and the working
 * — trial balance, cash book, mappings — one click away for whoever checks it.
 *
 * THE GRID (close.account_preparation@2) sits between the accounts and the
 * working: every bank line in the pattern an accountant decides on, month by
 * month, with how it was treated and why. Work prepared by @1 has none.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { FigureTraceResponse, WorkActionResponse, type FigureTrace, type PreparationView, type WorkFile } from "@leveri/contracts";
import { Button, Citation, Figure, Label, Notice, RuledList, Section, SectionHead, Select, StateText, Statement, TextField, markerTone } from "../../file/index.js";
import { call, failureMessage } from "../../api.js";
import { useKeyring } from "./keys.js";
import { RemapColumns } from "./mapColumns.js";
import { AccountPicker } from "./accountPicker.js";

const FIGURE_STRIP = "mt-6 grid grid-cols-1 border-y border-rule sm:grid-cols-2 lg:grid-cols-4";
const SUMMARY = "cursor-pointer list-none text-[15px] font-semibold text-ink hover:underline";
const NUM = "text-right [font-variant-numeric:tabular-nums]";

/** 1 — the answer, the figures it is about, and the basis it was prepared on. */
export function PreparationLead({ preparation }: { preparation: PreparationView }) {
  return (
    <Section id="figures">
      <Statement>{preparation.headline}</Statement>
      <p className="mt-3 text-[13px] text-stone">
        <StateText tone={markerTone(preparation.tone)}>{preparation.verdictLabel}</StateText>
        {" · "}{preparation.periodicityLabel} · {preparation.basisLabel}{preparation.periodText ? ` · ${preparation.periodText}` : ""}
      </p>
      {preparation.figures.length > 0 && (
        <div className={FIGURE_STRIP}>
          {preparation.figures.map((figure, index) => (
            <div key={figure.key} className={`py-4 ${index > 0 ? "lg:border-l lg:border-rule-soft lg:pl-6" : ""}`}>
              <div className="text-[12px] text-stone">{figure.label}</div>
              <div className="mt-1.5 font-mono text-[19px] text-ink [font-variant-numeric:tabular-nums]">{figure.text}</div>
              {figure.note && <p className="mt-1 text-[12px] text-stone">{figure.note}</p>}
            </div>
          ))}
        </div>
      )}
      {preparation.memorySentence && <p className="mt-3 text-[13px] text-stone">{preparation.memorySentence}</p>}
    </Section>
  );
}

/**
 * THE WORK PROGRAMME (2026-10-15): the objective, each procedure in a professional's order with where this work stands
 * on it, and the boundaries — all the server's words and statuses (`work.method`); this only lays them out. One line of
 * counts, the steps beneath it, the boundaries folded away.
 */
export function PreparationMethod({ method }: { method: NonNullable<WorkFile["method"]> }) {
  return (
    <Section id="programme">
      <SectionHead aside={<span className="text-[13px] text-stone" data-testid="programme-summary">{method.summary}</span>}>The work programme</SectionHead>
      <p className="max-w-[760px] text-[14px] text-ink-soft">{method.objective}</p>
      <ol className="mt-3 divide-y divide-rule-soft" data-testid="programme-steps">
        {method.steps.map((step, index) => (
          <li key={step.key} className="grid grid-cols-[1.75rem_1fr] gap-x-2 py-2.5 sm:grid-cols-[1.75rem_1fr_11rem]" data-testid={`programme-${step.key}`}>
            <span className="text-[13px] text-stone [font-variant-numeric:tabular-nums]">{index + 1}.</span>
            <div className="min-w-0">
              <p className="text-[14px] font-medium text-ink" title={step.detail}>{step.title}</p>
              {step.stands && <p className="mt-0.5 text-[13px] text-ink-soft">{step.stands}</p>}
            </div>
            <span className="col-start-2 mt-1 text-[13px] sm:col-start-3 sm:mt-0 sm:text-right">
              <StateText tone={markerTone(step.tone)}>{step.statusLabel}</StateText>
            </span>
          </li>
        ))}
      </ol>
      <details className="mt-2">
        <summary className="cursor-pointer list-none text-[13px] text-stone hover:underline">What Leveri will not do</summary>
        <ul className="mt-1.5 space-y-1">{method.boundaries.map((b) => <li key={b} className="text-[13px] text-ink-soft">{b}</li>)}</ul>
      </details>
    </Section>
  );
}

/**
 * 2 — what only the accountant can settle, and PREPARE AGAIN. The button is
 * offered only when the server says the work can be prepared again (settled
 * work, review authority, at least one decision on this version); otherwise
 * the server's sentence says why the decisions are not applied yet.
 */
export function PreparationDecide({ work, preparation, onWork, onOutcome }: {
  work: WorkFile;
  preparation: PreparationView;
  onWork: (work: WorkFile) => void;
  onOutcome: (outcome: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const again = preparation.prepareAgain;
  const [remapping, setRemapping] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const keyFor = useKeyring();
  // F-02: the remembered classes this person may confirm now — decisions of this version, open, theirs to review.
  const confirmable = useMemo(() => {
    const mine = new Set(work.conclusions.filter((c) => c.canReview && !c.review.act).map((c) => c.id));
    return preparation.remembered.decisionIds.filter((id) => mine.has(id));
  }, [work.conclusions, preparation.remembered.decisionIds]);
  if (preparation.reviewerItems.length === 0 && !again.sentence && preparation.columnsRead.length === 0) return null;
  // The server splits every version's list (what is to decide, what is only noted); a remembered class to confirm is
  // said in its own place below, never in this list.
  const decide = preparation.decideItems;
  const noted = preparation.notedItems;

  /** Confirm every remembered class as it was: a review act each, in this person's name, a hundred at a time. */
  async function confirmRemembered() {
    const versionId = work.review.versionId;
    if (confirming || !versionId || confirmable.length === 0) return;
    setConfirming(true);
    setFailure(null);
    try {
      let last: { outcome: string; work: WorkFile } | null = null;
      for (let i = 0; i < confirmable.length; i += 100) {
        const chunk = confirmable.slice(i, i + 100);
        last = await call("POST", `/api/work/${work.id}/conclusions/review-many`, WorkActionResponse, {
          artifactVersionId: versionId,
          basis: `Confirmed for this period, as decided in earlier work (${chunk.length} remembered class${chunk.length === 1 ? "" : "es"}).`,
          items: chunk.map((decisionId) => ({ decisionId, outcome: "ACCEPTED" })),
          submissionKey: keyFor(`remembered:${chunk.join(",")}`.slice(0, 120)),
        });
      }
      if (last) { onOutcome(last.outcome); onWork(last.work); }
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setConfirming(false);
    }
  }

  async function prepareAgain() {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      const result = await call("POST", `/api/work/${work.id}/prepare-again`, WorkActionResponse);
      onOutcome(result.outcome);
      onWork(result.work);
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section id="decide">
      {/* A count, then the lists closed: a long list never pushes the work off the screen (a tester's report, 2026-10-15). */}
      {preparation.reviewerItems.length > 0 && (
        <>
          <SectionHead>For you to decide</SectionHead>
          {preparation.decideSummary && (
            <p className="text-[15px] text-ink" data-testid="decide-summary">
              {preparation.decideSummary}. <a href="#review" className="underline">Decide them in your review</a>
            </p>
          )}
          {decide.length > 0 && (
            <details className="mt-3" data-testid="decide-items">
              <summary className={SUMMARY}>Show the {decide.length === 1 ? "item" : `${decide.length} items`}</summary>
              <RuledList>
                {decide.map((item, index) => (
                  <p key={`${index}-${item.slice(0, 24)}`} className="py-2.5 text-[14px] text-ink">{item}</p>
                ))}
              </RuledList>
            </details>
          )}
          {noted.length > 0 && (
            <details className="mt-2" data-testid="noted-items">
              <summary className="cursor-pointer list-none text-[13px] text-stone hover:underline">
                Noted: {noted.length} {noted.length === 1 ? "record or answer" : "records and answers"} the draft is prepared with
              </summary>
              <RuledList>
                {noted.map((item, index) => (
                  <p key={`${index}-${item.slice(0, 24)}`} className="py-2 text-[13px] text-stone">{item}</p>
                ))}
              </RuledList>
            </details>
          )}
          {preparation.remembered.sentence && (
            <div className="mt-3" data-testid="remembered">
              <p className="text-[14px] text-ink">{preparation.remembered.sentence}</p>
              {confirmable.length > 0 && (
                <Button className="mt-2" variant="secondary" disabled={confirming} onClick={() => void confirmRemembered()} data-testid="confirm-remembered">
                  {confirming ? "Confirming…" : `Confirm ${confirmable.length === 1 ? "it" : `all ${confirmable.length}`}`}
                </Button>
              )}
            </div>
          )}
          <p className="mt-2 text-[13px] text-stone">Your decisions are remembered for this client. The next period uses them and asks you once to confirm them; the client is not asked again.</p>
        </>
      )}
      {/* LAYOUT MEMORY: statements read with columns a person mapped earlier — said, and mapped again in place. */}
      {preparation.columnsRead.length > 0 && (
        <div className={preparation.reviewerItems.length > 0 ? "mt-5" : ""} data-testid="columns-read">
          <Label className="mb-1">How the statements were read</Label>
          {preparation.columnsRead.map((read) => {
            const id = read.sheet ? `${read.sheet.evidenceReferenceId}:${read.sheet.sheetName}` : read.sentence;
            return (
              <div key={id} className="border-t border-rule-soft py-2.5 first:border-t-0">
                <p className="text-[14px] text-ink">{read.sentence}</p>
                {preparation.mayRemap && read.sheet && remapping !== id && (
                  <button type="button" className="mt-1 text-[13px] text-ink underline" onClick={() => setRemapping(id)} data-testid="remap-columns">
                    Map differently
                  </button>
                )}
                {read.sheet && remapping === id && (
                  <RemapColumns workId={work.id} sheet={read.sheet} onWork={(next) => { setRemapping(null); onWork(next); }} onOutcome={onOutcome} />
                )}
              </div>
            );
          })}
        </div>
      )}
      {again.sentence && (
        <div className={preparation.reviewerItems.length > 0 ? "mt-5" : ""}>
          <p className="text-sm text-ink" data-testid="prepare-again-sentence">{again.sentence}</p>
          {again.available && (
            <div className="mt-3">
              <Button disabled={busy} onClick={() => void prepareAgain()} data-testid="prepare-again">
                {busy ? "Preparing again…" : "Prepare again with my decisions"}
              </Button>
            </div>
          )}
        </div>
      )}
      {failure && <Notice className="mt-3">{failure}</Notice>}
    </Section>
  );
}

/**
 * WHAT MAKES UP A FIGURE (accountant interviews, F-03): the accountant's test of anyone's work is to pick a figure and
 * ask what makes it up. A line whose version keeps its entries opens, in place, to the server's list of them.
 */
function Block({ block, first, workId }: { block: PreparationView["profitAndLoss"]["blocks"][number]; first: boolean; workId: string }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className={first ? "" : "mt-5"}>
      <Label className="mb-1">{block.label}</Label>
      {block.lines.map((line, index) => {
        const account = line.account;
        const opened = account !== null && open === account;
        return (
          <div key={`${line.label}-${index}`}>
            <Figure
              first={index === 0}
              label={account === null ? line.label : (
                <button type="button" className={TRACE} aria-expanded={opened} onClick={() => setOpen(opened ? null : account)}>
                  {line.label}
                </button>
              )}
              value={line.text}
            />
            {opened && <FigureEntries workId={workId} account={account} onClose={() => setOpen(null)} />}
          </div>
        );
      })}
      <Figure label={`Total ${block.label.toLowerCase()}`} value={block.totalText} total />
    </div>
  );
}

const TRACE = "text-left text-ink underline decoration-rule-strong decoration-1 underline-offset-4 hover:decoration-ink";
const FIRST_ENTRIES = 50;

/** The entries behind one figure, as the server lists them: each with its day, its words, why it is there, and its rows. */
function FigureEntries({ workId, account, onClose }: { workId: string; account: string; onClose: () => void }) {
  const [trace, setTrace] = useState<FigureTrace | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  useEffect(() => {
    let live = true;
    call("GET", `/api/work/${workId}/figure?account=${encodeURIComponent(account)}`, FigureTraceResponse)
      .then((r) => { if (live) setTrace(r.figure); })
      .catch((e: unknown) => { if (live) setFailure(failureMessage(e)); });
    return () => { live = false; };
  }, [workId, account]);
  const shown = trace ? (all ? trace.entries : trace.entries.slice(0, FIRST_ENTRIES)) : [];
  return (
    <div className="mb-3 border-l-2 border-rule-strong pl-4" role="region" aria-label="What makes up this figure">
      {failure && <Notice className="mt-2">{failure}</Notice>}
      {!trace && !failure && <p className="py-2 text-[13px] text-stone">Opening the entries…</p>}
      {trace && (
        <>
          <p className="pt-2 text-[13px]">
            {trace.agrees ? <span className="text-ink-soft">{trace.sentence}</span> : <StateText tone={markerTone("danger")}>{trace.sentence}</StateText>}
          </p>
          <ul className="mt-1">
            {shown.map((entry, index) => (
              <li key={`${entry.date}-${index}`} className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-4 border-t border-rule-soft py-2 first:border-t-0">
                <div className="min-w-0 text-[13px]">
                  <span className="text-stone">{entry.dateText}</span>
                  {entry.words ? <span className="ml-2 text-ink">{entry.words}</span> : null}
                  {entry.basis ? <span className="block text-stone">{entry.basis}</span> : null}
                  {entry.sources.length > 0 && <Citation className="block break-words">{entry.sources.join(" · ")}</Citation>}
                </div>
                <div className={`text-[13px] text-ink ${NUM}`}>{entry.amountText}</div>
              </li>
            ))}
          </ul>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-4 gap-y-1 pb-1 text-[13px]">
            {!all && trace.entries.length > FIRST_ENTRIES && (
              <button type="button" className={TRACE} onClick={() => setAll(true)}>Show all {trace.entries.length} entries</button>
            )}
            <button type="button" className={TRACE} onClick={onClose}>Close</button>
          </div>
        </>
      )}
    </div>
  );
}

/** 3 — the accounts, and the working behind them. */
export function PreparationBooks({ preparation, work, onWork, onOutcome }: {
  preparation: PreparationView;
  work: WorkFile;
  onWork: (work: WorkFile) => void;
  onOutcome: (outcome: string) => void;
}) {
  const pl = preparation.profitAndLoss;
  const bs = preparation.balanceSheet;
  return (
    <>
      <Section id="profit-and-loss">
        <SectionHead>Profit and loss</SectionHead>
        {pl.blocks.length === 0 ? (
          <p className="text-sm text-stone">Nothing has been classified to income or expenses yet.</p>
        ) : (
          pl.blocks.map((block, index) => <Block key={block.key} block={block} first={index === 0} workId={work.id} />)
        )}
        <Figure className="mt-5" label={pl.netProfitMinorUnits >= 0 ? "Profit for the period" : "Loss for the period"} value={pl.netProfitText} total />
      </Section>

      <Section id="balance-sheet">
        <SectionHead>Balance sheet</SectionHead>
        {bs.blocks.map((block, index) => <Block key={block.key} block={block} first={index === 0} workId={work.id} />)}
        <Figure className="mt-5" label="Assets" value={bs.assetsText} total />
        <Figure label="Liabilities and equity" value={bs.fundingText} total />
        {!bs.balanced && (
          <p className="mt-3 text-[13px]"><StateText tone={markerTone("danger")}>The draft balance sheet does not balance. It cannot be relied on until the difference is found.</StateText></p>
        )}
      </Section>

      {preparation.grid.rows.length > 0 && <PatternGrid grid={preparation.grid} chart={preparation.chart} accountTypes={preparation.accountTypes} work={work} onWork={onWork} onOutcome={onOutcome} />}

      <Section id="working">
        <SectionHead>The working</SectionHead>
        <div className="flex flex-col gap-5">
          <details>
            <summary className={SUMMARY}>
              Draft trial balance{" "}
              <span className="ml-1 text-[13px] font-normal text-stone">
                {preparation.trialBalanceTotals.balanced ? "balances" : "does not balance"} · {preparation.trialBalanceTotals.debitText} debit · {preparation.trialBalanceTotals.creditText} credit
              </span>
            </summary>
            {/* Wide on purpose: six columns. It scrolls inside its own box on a phone, never the page. */}
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[640px] text-[13px]">
                <thead>
                  <tr className="border-b border-rule text-left text-stone">
                    <th className="py-2 pr-3 font-normal">Account</th>
                    <th className="py-2 pr-3 font-normal">Group</th>
                    <th className={`py-2 pr-3 font-normal ${NUM}`}>Opening</th>
                    <th className={`py-2 pr-3 font-normal ${NUM}`}>Debit</th>
                    <th className={`py-2 pr-3 font-normal ${NUM}`}>Credit</th>
                    <th className={`py-2 font-normal ${NUM}`}>Closing</th>
                  </tr>
                </thead>
                <tbody>
                  {preparation.trialBalance.map((row) => (
                    <tr key={row.account} className="border-b border-rule-soft text-ink">
                      <td className="py-2 pr-3">{row.label}</td>
                      <td className="py-2 pr-3 text-stone">{row.groupLabel}</td>
                      <td className={`whitespace-nowrap py-2 pr-3 ${NUM}`}>{row.openingText}</td>
                      <td className={`whitespace-nowrap py-2 pr-3 ${NUM}`}>{row.debitText}</td>
                      <td className={`whitespace-nowrap py-2 pr-3 ${NUM}`}>{row.creditText}</td>
                      <td className={`whitespace-nowrap py-2 ${NUM}`}>{row.closingText}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>

          <details>
            <summary className={SUMMARY}>
              Cash book by class <span className="ml-1 text-[13px] font-normal text-stone">{preparation.cashBook.length}</span>
            </summary>
            <div className="mt-3">
              {preparation.cashBook.map((row, index) => (
                <Figure
                  key={`${row.accountLabel}-${row.classLabel}-${index}`}
                  first={index === 0}
                  label={row.classLabel}
                  citation={`${row.accountLabel} · ${row.lines} line${row.lines === 1 ? "" : "s"}`}
                  value={row.totalText}
                />
              ))}
            </div>
          </details>

          {preparation.mappings.length > 0 && (
            <details>
              <summary className={SUMMARY}>
                The trial balance&rsquo;s accounts, mapped <span className="ml-1 text-[13px] font-normal text-stone">{preparation.mappings.length}</span>
              </summary>
              <RuledList className="mt-3">
                {preparation.mappings.map((m) => (
                  <div key={m.account} className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 py-2.5 text-[15px]">
                    <span className="text-ink">{m.label}</span>
                    <span className="text-[13px] text-stone">{m.classLabel ?? "No class yet"} · {m.mappingLabel}</span>
                  </div>
                ))}
              </RuledList>
            </details>
          )}

          {preparation.notes.length > 0 && (
            <details>
              <summary className={SUMMARY}>
                Notes on the draft <span className="ml-1 text-[13px] font-normal text-stone">{preparation.notes.length}</span>
              </summary>
              <RuledList className="mt-3">
                {preparation.notes.map((note, index) => <p key={`${index}-${note.slice(0, 24)}`} className="py-2.5 text-sm text-ink-soft">{note}</p>)}
              </RuledList>
            </details>
          )}
        </div>
      </Section>
    </>
  );
}

/** The bank lines by pattern: months across, patterns down; a payee beneath its channel is indented. */
function PatternGrid({ grid, chart, accountTypes, work, onWork, onOutcome }: {
  grid: PreparationView["grid"]; chart: PreparationView["chart"]; accountTypes: PreparationView["accountTypes"];
  work: WorkFile; onWork: (work: WorkFile) => void; onOutcome: (outcome: string) => void;
}) {
  const [asking, setAsking] = useState(false);
  const [adopting, setAdopting] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  // CLASSIFY IN BULK (a tester's report, 2026-10-15): find the lines, select them, one class for all — each a review act.
  const [find, setFind] = useState("");
  const [onlyToClassify, setOnlyToClassify] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [bulkClass, setBulkClass] = useState("");
  const [applying, setApplying] = useState(false);
  const keyFor = useKeyring();
  const askedFor = useRef<number | null>(null);
  const version = work.workpaperVersion;
  const workId = work.id;
  /** The rows a person may classify here: a decision of the current version, not yet reviewed, and their authority to. */
  const classifiable = useMemo(() => {
    const open = new Set(work.conclusions.filter((c) => c.canReview && c.questionKind === "COUNTERPARTY_CLASSIFICATION").map((c) => c.id));
    return new Set(grid.rows.filter((r) => r.decisionId && open.has(r.decisionId)).map((r) => r.key));
  }, [grid.rows, work.conclusions]);
  const classOptions = useMemo(
    () => work.conclusions.find((c) => c.questionKind === "COUNTERPARTY_CLASSIFICATION" && c.canReview)?.adjustOptions ?? [],
    [work.conclusions],
  );
  /** Whether this viewer may review this work (the server decides it per conclusion; the split is refused server-side otherwise). */
  const mayReview = useMemo(() => work.conclusions.some((c) => c.canReview), [work.conclusions]);
  const shown = useMemo(() => {
    const words = find.trim().toLowerCase();
    return grid.rows.filter((r) => (!onlyToClassify || classifiable.size === 0 || classifiable.has(r.key)) && (!words || r.words.toLowerCase().includes(words)));
  }, [grid.rows, find, onlyToClassify, classifiable]);
  const shownSelectable = useMemo(() => shown.filter((r) => classifiable.has(r.key)), [shown, classifiable]);
  const allShownSelected = shownSelectable.length > 0 && shownSelectable.every((r) => selected.has(r.key));
  const toggle = (key: string) => setSelected((now) => { const next = new Set(now); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  const toggleShown = () => setSelected((now) => {
    const next = new Set(now);
    for (const r of shownSelectable) { if (allShownSelected) next.delete(r.key); else next.add(r.key); }
    return next;
  });
  /** "All similar": the lines whose words begin as this one's do — found, shown and selected in one press. */
  const similar = (row: PreparationView["grid"]["rows"][number]) => {
    const first = row.words.trim().split(/\s+/);
    const stem = (first[0] && first[0].length <= 3 && first[1] ? `${first[0]} ${first[1]}` : first[0] ?? "").toLowerCase();
    setFind(stem);
    setSelected(new Set(grid.rows.filter((r) => classifiable.has(r.key) && r.words.toLowerCase().includes(stem)).map((r) => r.key)));
  };

  /** One class for every selected pattern: a review act each, in this person's name, sent a hundred at a time. */
  async function applyBulk() {
    const versionId = work.review.versionId;
    const chosen = classOptions.find((o) => o.conclusionKind === bulkClass);
    const rows = grid.rows.filter((r) => selected.has(r.key) && r.decisionId && classifiable.has(r.key));
    if (applying || !versionId || !chosen || rows.length === 0) return;
    setApplying(true); setFailure(null);
    try {
      let last: { outcome: string; work: WorkFile } | null = null;
      const basis = `Classified as ${chosen.label} in the classification table, with ${rows.length} pattern${rows.length === 1 ? "" : "s"}${find.trim() ? ` found by “${find.trim()}”` : ""}.`;
      for (let i = 0; i < rows.length; i += 100) {
        const chunk = rows.slice(i, i + 100);
        last = await call("POST", `/api/work/${work.id}/conclusions/review-many`, WorkActionResponse, {
          artifactVersionId: versionId, basis,
          items: chunk.map((r) => ({ decisionId: r.decisionId!, outcome: "ADJUSTED", adjustedConclusion: { conclusionKind: chosen.conclusionKind, conclusionText: chosen.label } })),
          // The same selection pressed twice is one act; each pattern is keyed on its own by the server.
          submissionKey: keyFor(`bulk:${chosen.conclusionKind}:${chunk.map((r) => r.decisionId).join(",")}`.slice(0, 120)),
        });
      }
      if (last) { onOutcome(last.outcome); onWork(last.work); }
      setSelected(new Set()); setBulkClass("");
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setApplying(false);
    }
  }
  // Suggestions are asked for ONCE per version, when the server offers them: Leveri works without being told to.
  useEffect(() => {
    if (!grid.suggestionsOffered || version === null || askedFor.current === version) return;
    askedFor.current = version;
    setAsking(true);
    call("POST", `/api/work/${workId}/classification-suggestions`, WorkActionResponse)
      .then((r) => onWork(r.work))
      .catch(() => undefined)
      .finally(() => setAsking(false));
  }, [grid.suggestionsOffered, version, workId, onWork]);

  /** Adopting a suggestion is the governed review act, adjusting the decision to the suggested class — a person's act, recorded as theirs. */
  async function adopt(row: PreparationView["grid"]["rows"][number]) {
    if (!row.decisionId || !row.suggestion || !work.review.versionId) return;
    setAdopting(row.key); setFailure(null);
    try {
      const r = await call("POST", `/api/work/${work.id}/conclusions/${row.decisionId}/review`, WorkActionResponse, {
        artifactVersionId: work.review.versionId, outcome: "ADJUSTED",
        adjustedConclusion: { conclusionKind: row.suggestion.classKey, conclusionText: row.suggestion.basis },
        basis: `Adopted Leveri's suggestion (${row.suggestion.model}): ${row.suggestion.basis}`,
        submissionKey: `adopt-suggestion:${row.decisionId}`,
      });
      onOutcome(r.outcome); onWork(r.work);
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setAdopting(null);
    }
  }
  const mayAdopt = (row: PreparationView["grid"]["rows"][number]) =>
    Boolean(row.decisionId && work.conclusions.find((c) => c.id === row.decisionId)?.canReview);
  const suggestionOf = (row: PreparationView["grid"]["rows"][number]) => row.suggestion && (
    <div className="mt-1.5 border-l-2 border-rule pl-2.5" data-testid="pattern-suggestion">
      <p className="text-[12px] text-ink-soft">Leveri suggests <span className="font-medium text-ink">{row.suggestion.classLabel}</span>{row.suggestion.aboveMateriality ? " · above materiality: review it individually" : ""}</p>
      <p className="text-[12px] leading-snug text-stone">{row.suggestion.basis} <span className="whitespace-nowrap">(suggested by {row.suggestion.model})</span></p>
      {mayAdopt(row) && (
        <button type="button" disabled={adopting !== null} onClick={() => void adopt(row)} className="mt-1 text-[12px] font-medium text-forest hover:underline disabled:opacity-60">
          {adopting === row.key ? "Adopting…" : "Use this"}
        </button>
      )}
    </div>
  );
  /** Post a pattern's lines to an account of the client's chart, or one created here (a tester's report, 2026-10-21). */
  const postTo = (row: PreparationView["grid"]["rows"][number]) => (
    <AccountPicker workId={work.id} target={{ kind: "PATTERN", key: row.key }} chart={chart} accountTypes={accountTypes}
      about={`${row.lineCount === 1 ? "this line" : `these ${row.lineCount} lines`} (${row.words})`} onWork={onWork} onOutcome={onOutcome} />
  );
  return (
    <Section id="patterns">
      <SectionHead>Bank lines, by pattern</SectionHead>
      <p className="-mt-2 mb-4 text-[13px] text-stone">
        {grid.rows.length} pattern{grid.rows.length === 1 ? "" : "s"}
        {grid.toClassify > 0 ? ` · ${grid.toClassify} to classify` : " · every line treated"}
        {asking ? " · Leveri is suggesting classes…" : ""}
      </p>
      {failure && <Notice className="mb-3">{failure}</Notice>}
      {/* Find the lines, then classify them together: every selected pattern gets the class, as your review of each. */}
      {classifiable.size > 0 && (
        <div className="mb-4 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end" data-testid="bulk-tools">
          <TextField label="Find lines" placeholder="Words in the narration, e.g. transfer" value={find} onChange={(e) => setFind(e.target.value)} data-testid="bulk-find" />
          <label className="inline-flex items-center gap-2 pb-2 text-[13px] text-ink">
            <input type="checkbox" className="accent-forest" checked={onlyToClassify} onChange={(e) => setOnlyToClassify(e.target.checked)} />
            Only what is still to classify
          </label>
        </div>
      )}
      {selected.size > 0 && (
        <div className="sticky top-16 z-20 mb-4 flex flex-wrap items-end gap-3 rounded-control border border-rule bg-canvas p-3 shadow-sm" data-testid="bulk-bar">
          <p className="pb-2 text-[14px] text-ink">{selected.size} pattern{selected.size === 1 ? "" : "s"} selected</p>
          <div className="min-w-[220px] flex-1">
            <Select label="Classify them as" value={bulkClass} disabled={applying} onChange={(e) => setBulkClass(e.target.value)} data-testid="bulk-class">
              <option value="">Choose a class</option>
              {classOptions.map((o) => <option key={o.conclusionKind} value={o.conclusionKind}>{o.label}</option>)}
            </Select>
          </div>
          <Button disabled={applying || !bulkClass} onClick={() => void applyBulk()} data-testid="bulk-apply">{applying ? "Recording…" : "Apply to the selected"}</Button>
          <button type="button" className="pb-2 text-[13px] text-stone hover:underline" disabled={applying} onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}
      {shown.length === 0 && <p className="text-[13px] text-stone">No pattern matches.</p>}
      {/* On a phone each pattern is a row of its own; from tablet width up, months run across. */}
      <ul className="sm:hidden" data-testid="pattern-list">
        {shown.map((row) => (
          <li key={row.key} className={`border-b border-rule-soft py-3 ${row.level === "PAYEE" && row.channelKey ? "pl-4" : ""}`}>
            <div className="flex items-baseline justify-between gap-3">
              <span className={`min-w-0 text-[14px] text-ink ${row.level === "CHANNEL" ? "font-semibold" : ""}`}>
                {classifiable.has(row.key) && (
                  <input type="checkbox" className="mr-2 accent-forest" aria-label={`Select ${row.words}`} checked={selected.has(row.key)} onChange={() => toggle(row.key)} />
                )}
                {row.words}
              </span>
              <span className={`shrink-0 whitespace-nowrap text-[14px] text-ink ${NUM}`}>{row.totalText}</span>
            </div>
            <p className="text-[12px] text-stone">{row.direction === "IN" ? "In" : "Out"} · {row.lineCount} line{row.lineCount === 1 ? "" : "s"}{grid.months.length > 1 ? ` · ${row.cells.filter(Boolean).length} of ${grid.months.length} months` : ""}</p>
            <p className="mt-1 text-[13px]"><StateText tone={markerTone(row.treatment.state === "ASKED" ? "warn" : row.treatment.state === "MIXED" || row.treatment.state === "EXCLUDED" ? "neutral" : "ok")}>{row.treatment.label}</StateText></p>
            {row.treatment.basis && <p className="mt-0.5 text-[12px] leading-snug text-stone">{row.treatment.basis}</p>}
            {suggestionOf(row)}
            {classifiable.has(row.key) && <button type="button" className="mt-1 text-[12px] font-medium text-forest hover:underline" onClick={() => similar(row)}>Select similar</button>}
            {mayReview && row.lineCount === 1 && row.level !== "CHANNEL" && classOptions.length > 0 && <SplitLine workId={work.id} row={row} classOptions={classOptions} onWork={onWork} onOutcome={onOutcome} />}
            {mayReview && postTo(row)}
          </li>
        ))}
      </ul>
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full min-w-[640px] text-[13px]" data-testid="pattern-grid">
          <thead>
            <tr className="border-b border-rule text-left text-stone">
              {classifiable.size > 0 && (
                <th className="w-8 py-2 pr-2 font-normal">
                  <input type="checkbox" className="accent-forest" aria-label="Select every pattern shown" checked={allShownSelected} onChange={toggleShown} data-testid="bulk-select-shown" />
                </th>
              )}
              <th className="py-2 pr-3 font-normal">Pattern</th>
              {grid.months.map((m) => <th key={m.key} className={`py-2 pr-3 font-normal ${NUM}`}>{m.label}</th>)}
              {grid.months.length > 1 && <th className={`py-2 pr-3 font-normal ${NUM}`}>Total</th>}
              <th className="py-2 font-normal">Treated as</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => (
              <tr key={row.key} className="border-b border-rule-soft align-top text-ink">
                {classifiable.size > 0 && (
                  <td className="py-2.5 pr-2">
                    {classifiable.has(row.key) && (
                      <input type="checkbox" className="accent-forest" aria-label={`Select ${row.words}`} checked={selected.has(row.key)} onChange={() => toggle(row.key)} data-testid="bulk-select" />
                    )}
                  </td>
                )}
                <td className={`py-2.5 pr-3 ${row.level === "PAYEE" && row.channelKey ? "pl-5 text-ink-soft" : ""}`}>
                  <span className={row.level === "CHANNEL" ? "font-semibold" : ""}>{row.words}</span>
                  <span className="block text-[12px] text-stone">
                    {row.direction === "IN" ? "In" : "Out"} · {row.lineCount} line{row.lineCount === 1 ? "" : "s"}
                    {classifiable.has(row.key) && <> · <button type="button" className="font-medium text-forest hover:underline" onClick={() => similar(row)}>Select similar</button></>}
                  </span>
                </td>
                {row.cells.map((cell, i) => <td key={grid.months[i]!.key} className={`whitespace-nowrap py-2.5 pr-3 ${NUM}`}>{cell ?? <span className="text-stone">—</span>}</td>)}
                {grid.months.length > 1 && <td className={`whitespace-nowrap py-2.5 pr-3 font-medium ${NUM}`}>{row.totalText}</td>}
                <td className="py-2.5 min-w-[220px]">
                  <StateText tone={markerTone(row.treatment.state === "ASKED" ? "warn" : row.treatment.state === "MIXED" || row.treatment.state === "EXCLUDED" ? "neutral" : "ok")}>{row.treatment.label}</StateText>
                  {row.treatment.basis && <span className="mt-0.5 block text-[12px] leading-snug text-stone">{row.treatment.basis}</span>}
                  {suggestionOf(row)}
                  {mayReview && row.lineCount === 1 && row.level !== "CHANNEL" && classOptions.length > 0 && <SplitLine workId={work.id} row={row} classOptions={classOptions} onWork={onWork} onOutcome={onOutcome} />}
                  {mayReview && postTo(row)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

/**
 * SPLIT ONE BANK LINE ACROSS CLASSES (a tester's report, 2026-10-15): "the reviewer should be able to split the amount
 * across account heads … the split amounts must add up to the original transaction amount." Two or more parts, each a
 * class and an amount; the server checks that they add up to exactly the line and that each class takes money that
 * way, files the split in the person's name and prepares the accounts again. Nothing is added up here.
 */
function SplitLine({ workId, row, classOptions, onWork, onOutcome }: {
  workId: string; row: PreparationView["grid"]["rows"][number]; classOptions: ReadonlyArray<{ conclusionKind: string; label: string }>;
  onWork: (work: WorkFile) => void; onOutcome: (outcome: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [parts, setParts] = useState<Array<{ classKey: string; amount: string }>>([{ classKey: "", amount: "" }, { classKey: "", amount: "" }]);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const keyFor = useKeyring();
  const ready = parts.length >= 2 && parts.every((p) => p.classKey && /^\d{1,15}(\.\d{1,2})?$/.test(p.amount.replace(/,/g, "").trim()));
  const set = (i: number, patch: Partial<{ classKey: string; amount: string }>) => setParts((now) => now.map((p, k) => (k === i ? { ...p, ...patch } : p)));

  async function send() {
    if (busy || !ready) return;
    setBusy(true); setFailure(null);
    try {
      const body = parts.map((p) => ({ classKey: p.classKey, amount: p.amount.replace(/,/g, "").trim() }));
      const result = await call("POST", `/api/work/${workId}/lines/split`, WorkActionResponse, {
        patternKey: row.key, parts: body, submissionKey: keyFor(`split:${row.key}:${JSON.stringify(body)}`),
      });
      onOutcome(result.outcome);
      onWork(result.work);
      setOpen(false);
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return <button type="button" className="mt-1 block text-[12px] font-medium text-forest hover:underline" onClick={() => setOpen(true)} data-testid="split-line">Split this line</button>;
  }
  return (
    <div className="mt-2 border-t border-rule-soft pt-2" data-testid="split-line-form">
      <p className="text-[12px] text-stone">Split {row.totalText} across classes. The parts must add up to exactly {row.totalText}.</p>
      {parts.map((p, i) => (
        <div key={i} className="mt-2 flex flex-wrap items-end gap-2">
          <div className="min-w-[160px] flex-1">
            <Select label={`Part ${i + 1}`} value={p.classKey} disabled={busy} onChange={(e) => set(i, { classKey: e.target.value })} data-testid={`split-class-${i}`}>
              <option value="">Choose a class</option>
              {classOptions.map((o) => <option key={o.conclusionKind} value={o.conclusionKind}>{o.label}</option>)}
            </Select>
          </div>
          <label className="w-[130px] text-[12px] text-stone">
            Amount
            <input type="text" inputMode="decimal" value={p.amount} disabled={busy} onChange={(e) => set(i, { amount: e.target.value })}
              className={`mt-1 block w-full border border-line-strong bg-raised px-2 py-1.5 text-[13px] text-ink ${NUM}`} data-testid={`split-amount-${i}`} />
          </label>
          {parts.length > 2 && <button type="button" className="pb-2 text-[12px] text-stone hover:underline" disabled={busy} onClick={() => setParts((now) => now.filter((_, k) => k !== i))}>Remove</button>}
        </div>
      ))}
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <Button size="sm" disabled={busy || !ready} onClick={() => void send()} data-testid="split-send">{busy ? "Splitting…" : "Split it"}</Button>
        {parts.length < 6 && <button type="button" className="text-[12px] text-stone hover:underline" disabled={busy} onClick={() => setParts((now) => [...now, { classKey: "", amount: "" }])}>Add a part</button>}
        <button type="button" className="text-[12px] text-stone hover:underline" disabled={busy} onClick={() => { setOpen(false); setFailure(null); }}>Cancel</button>
      </div>
      {failure && <div className="mt-2"><Notice>{failure}</Notice></div>}
    </div>
  );
}
